import { useCallback } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import ProductDesign from "../../Products/ProductDesign"
import ProductDesignLinkedStoreIcon from "../../Products/ProductDesignLinkedStoreIcon"
import { useAppDispatch } from "../../../Common/_hooks/useAppDispatch"
import { EditProductListing, RemoveProductListing } from "../../../UserData/_actions/Store/StoreProductListingActions"
import type { UserProductDesignStore } from "../../../UserData/_stores/UserProductDesignStore"
import { useAppSelector } from "../../../Common/_hooks/useAppSelector"
import type { ProductLayoutType } from "../../../UserData/_stores/UserPrefsStore"
import buildDefaultProductListing from "./Utils/buildDefaultProductListing"

interface Props {
  view: ProductLayoutType
  item: UserProductDesignStore
  store: string
  getSelectionIds: (id: number, shiftKey?: boolean) => number[]
}

export default function ProductListingCard(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const products = useAppSelector(state => state.get('appData').get('products'))
  const productDesigns = useAppSelector(state => state.get('userData').get('productDesigns').get('data'))
  const storePresets = useAppSelector(state => state.get('userData').get('storePresets'))
  const isSelected = useAppSelector(state => state.get('UIData').get('account').get('storeProductListings').has(String(props.item.get('id'))))

  const selectProduct = useCallback((id: number) => {
    const productDesign = productDesigns.get(String(id))
    if (!productDesign) return;

    const product = products.get(String(productDesign.get('id_product')))
    if(!product) return;

    const listing = buildDefaultProductListing(productDesign, product, storePresets.get(productDesign.get('id_product')));

    dispatch(EditProductListing(
      listing.id_product_design,
      listing.id_product,
      listing.title,
      listing.description,
      listing.price,
      listing.update_preset,
      listing.is_modified,
      listing.disabled_preview_ids,
      listing.disabled_option_ids,
      listing.variant_prices,
      undefined,
    ))
  }, [dispatch, productDesigns, products, storePresets])

  const onSelect = useCallback((id: number, shiftKey = false) => {
    props.getSelectionIds(id, shiftKey).forEach(selectProduct)
  }, [props.getSelectionIds, selectProduct])

  const onUnselect = useCallback((id: number, shiftKey = false) => {
    props.getSelectionIds(id, shiftKey).forEach(productDesignId => {
      dispatch(RemoveProductListing(String(productDesignId)))
    })
  }, [dispatch, props.getSelectionIds])

  const alreadyPublished = props.item.get('linkedStores').has(props.store)
  const alreadyPublishedIndicator = alreadyPublished ? <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    sx={{
      height: 24,
      width: 'fit-content',
      maxWidth: '100%',
      pr: 0.75,
      borderRadius: 3,
      border: '1px solid rgba(0, 0, 0, 0.12)',
      backgroundColor: '#fff',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 22,
        flexShrink: 0,

        '& > span': {
          width: 22,
          height: 22,
          border: 0,
          borderRadius: 0,
          boxSizing: 'border-box',
        },
      }}
    >
      <ProductDesignLinkedStoreIcon storeSlug={props.store} />
    </Box>
    <Typography
      variant="caption"
      color="text.primary"
      sx={{
        fontWeight: 500,
        lineHeight: '22px',
        whiteSpace: 'nowrap',
      }}
    >
      {t('Already published')}
    </Typography>
  </Stack> : undefined

  return <ProductDesign
    item={props.item}
    view={props.view}
    selected={isSelected}
    onSelect={onSelect}
    onUnselect={onUnselect}
    caption={props.view === 'list' ? alreadyPublishedIndicator : undefined}
    imageOverlay={props.view === 'grid' ? alreadyPublishedIndicator : undefined}
    imageOverlayAlign="left"
  />
}
